import { Alert, Autocomplete, Box, Stack, TextField, Tooltip, Typography } from "@mui/material";
import { type FC, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import type { UseSupabaseLoaderState } from "../../../../../server/supabase/hooks/types";
import { useDialog } from "../../../../../utils/hooks/use-dialog";
import type { UUID } from "../../../../../utils/uuid";
import { useSegmentCrudFunctions } from "../../../../segment/components/manager/hooks/use-segment-crud-functions";
import { NewSegmentModal } from "../../../../segment/components/manager/segment-modal/new-segment-modal";

import { useFlags } from "../../../../../server/optimizely";
import { SegmentCrudDataGridDialogV2New } from "../../../../segment/components/manager/segment-crud-data-grid-dialog-v2";
import type { SupabaseSegmentWithFavourite } from "../../../../segment/server/supabase-segment-service";
import type { Segment } from "../../../../segment/types";

type SegmentInfo = {
  selected: UUID | null;
  createNew: UUID;
};

type Option = { label: string; value: UUID; description?: string; favorite: boolean };

export const SegmentSelect: FC<{
  segmentLoader: UseSupabaseLoaderState<SupabaseSegmentWithFavourite[]>;
  onChange: (id: UUID | null) => void;
  segmentInfo?: SegmentInfo;
  allowNewSegment?: boolean;
  disabled?: boolean;
  loading?: boolean;
}> = ({
  segmentLoader: { loading: segmentLoaderLoading, error, data },
  onChange,
  segmentInfo,
  allowNewSegment = true,
  loading,
  disabled,
}) => {
  const {
    flags: { select_multiple_segments },
    flagsLoaded,
  } = useFlags("select_multiple_segments");

  const { t } = useTranslation();
  const {
    open: newSegmentModalOpen,
    handleOpen: openNewSegmentModal,
    handleClose: closeNewSegmentModal,
  } = useDialog();

  const { handleCreate } = useSegmentCrudFunctions({ is_recipient_type: false });

  const options = useMemo(() => {
    if (!data) return [];

    const elems = data.reduce((acc, segment) => {
      if (!segment.id || (!segment.name && segmentInfo?.selected !== segment.id)) return acc;

      acc.push({
        label: segment.name ?? t("Ad Hoc Segment"),
        value: segment.id,
        favorite: !!segment.favorite_segment,
        description: segment.description ?? "",
      });

      return acc;
    }, [] as Option[]);

    // favorites first, then sort by label
    elems.sort((a, b) => {
      if (a.favorite && !b.favorite) return -1;
      if (!a.favorite && b.favorite) return 1;
      return a.label.localeCompare(b.label);
    });

    if (allowNewSegment && segmentInfo?.createNew)
      elems.push({
        label: "New Segment",
        value: segmentInfo.createNew,
        favorite: false,
        description: "",
      });

    return elems;
  }, [data, segmentInfo, t, allowNewSegment]);

  const handleChange = useCallback(
    (_: React.SyntheticEvent, value: Option | null) => {
      if (value?.value === segmentInfo?.createNew) {
        openNewSegmentModal();
        return;
      }

      onChange(value?.value ?? null);
    },
    [onChange, openNewSegmentModal, segmentInfo?.createNew],
  );

  const saveSegment = useCallback(
    async (segment: Segment | null) => {
      const result = await handleCreate(segment);
      if (result.success) {
        closeNewSegmentModal();
        onChange(segment?.id ?? null);
      }

      return !result.success && result.errors ? result.errors : undefined;
    },
    [handleCreate, closeNewSegmentModal, onChange],
  );

  if (error) {
    console.error(error);
    return <Alert severity="error">{t("Error loading segments")}</Alert>;
  }

  if (!flagsLoaded) return null;

  return (
    <>
      <Autocomplete
        loading={segmentLoaderLoading || !flagsLoaded || loading}
        disabled={disabled || segmentLoaderLoading || loading}
        options={options}
        renderOption={(renderProps, option) =>
          option.label === "New Segment" ? (
            <Box component="li" {...renderProps} key={option.value}>
              <Typography sx={{ color: "primary.main", fontWeight: "bold" }}>
                {t("Create new segment")}
              </Typography>
            </Box>
          ) : (
            <Tooltip title={option.description} key={option.value}>
              <Box component="li" {...renderProps} key={option.value}>
                <Stack direction="row" spacing={1}>
                  <Typography>{option.label}</Typography>
                </Stack>
              </Box>
            </Tooltip>
          )
        }
        renderInput={(params) => (
          <TextField
            label={t("Segment")}
            placeholder={t("Segment")}
            variant="standard"
            {...params}
          />
        )}
        defaultValue={options.find((option) => option.value === segmentInfo?.selected) ?? null}
        getOptionLabel={(option) => option.label}
        groupBy={(option) => (option.favorite ? t("⭐ Favorites") : t("Existing Segments"))}
        onChange={handleChange}
      />
      {select_multiple_segments ? (
        <SegmentCrudDataGridDialogV2New
          open={newSegmentModalOpen}
          is_recipient_type={false}
          onClose={closeNewSegmentModal}
          nameRequired={false}
          onSave={onChange}
        />
      ) : (
        <NewSegmentModal
          open={newSegmentModalOpen}
          onSave={saveSegment}
          onCancel={closeNewSegmentModal}
          isRecipientType={false}
          requireName={false}
        />
      )}
    </>
  );
};
